import React, { useEffect, useState, Suspense } from "react";
import { VerificationLoader, SuspenseLoader } from "CommonComponent";
import {
  BrowserRouter as Router,
  useRoutes,
  useLocation,
  useNavigate
} from "react-router-dom";
import { verifyUserToken, logout, setCurrentPagePermissions, setCurrentObjectViewedId } from "actions";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty, filter, forEach } from 'lodash';
import { LOGIN_URL, DASHBOARD_URL } from 'configs';
import routes from "./routes";
import moment from "moment-timezone";
import { getObjectViewedIdFromRoute, resolveAccessObjectPath } from 'utils/menuUtils';

const  AppRoutes = () => {
  let location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const session = useSelector(state => state.session);

  //ignoring dashboard, auth and errors pages from validation.
  const [ignoredRouts, setIgnoredRouts] = useState([
    '/auth/login',
    '/auth/reset-password',
    '/users/reset-password-new-user',
    '/errors/error-404',
    '/unauthorized',
    '/errors/error-500',
    '/errors/error-404',
    '/errors/error-401',
  ]);

  useEffect(() => {
    if (isEmpty(location)) {
      return;
    }

    /*
     * checking role access
     */
    if (!ignoredRouts.includes(location.pathname)) {
      //checking jwt token
      if (isEmpty(localStorage.getItem("token"))) {
        localStorage.clear();
        window.location.replace(
          LOGIN_URL + "login?redirect_url=" + DASHBOARD_URL
        );
        return;
      }

      let raw_url = location.pathname.substring(1);
      let raw_parts = raw_url.split("/");
      let last_index = raw_parts[raw_parts.length - 1];

      //checking if url last chunk is (add/update/view)
      let page_type = "view";
      if (["add", "update", "edit", "view"].includes(last_index)) {
        page_type = last_index;
      }

      // Map nested form/view routes to the Access Management object_url.
      let current_url = resolveAccessObjectPath(
        location.pathname,
        location.state?.from || null
      );

      // Root and /dashboard both map to the dashboard permission object.
      if (location.pathname === "/" || location.pathname === "/dashboard") {
        current_url = "dashboard";
      }

      //deleting cache for datatable
      let lastPage = window.localStorage.getItem("lastPageVisit");

      if (current_url !== lastPage) {
        const items = { ...localStorage };

        forEach(items, function (value, key) {
          if (
            key.includes("_itemsPerPage") ||
            key.includes("_textSearch") ||
            key.includes("_columns") ||
            key.includes("_page")
          ) {
            window.localStorage.removeItem(key);
          } else if (key === "persist:root") {
          }
        });
      }

      // Extract object_viewed_id from nested_menus based on current route
      const object_viewed_id = getObjectViewedIdFromRoute(
        session.nested_menus,
        location.pathname,
        location.state?.from || null
      );
      if (object_viewed_id) {
        dispatch(setCurrentObjectViewedId(object_viewed_id));
      } else {
        // If no match found, try with the processed current_url (without add/update/view)
        const fallback_object_viewed_id = getObjectViewedIdFromRoute(
          session.nested_menus,
          `/${current_url}`
        );
        if (fallback_object_viewed_id) {
          dispatch(setCurrentObjectViewedId(fallback_object_viewed_id));
        } else {
          dispatch(setCurrentObjectViewedId(null));
        }
      }

      //find permissions against url
      let current_page;
      if (session.user.is_super_admin == "y") {
        current_page = filter(session.user_permissions_raw, function (item) {
          return item.object_url === current_url;
        });
      } else {
        current_page = filter(session.user_permissions_raw, function (item) {
          return item.object_url === current_url;
        });
      }

      //checking add/update/view access for that url
      if (!isEmpty(current_page)) {
        let current_page_permissions;
        if (session.user.is_super_admin === "y") {
          current_page_permissions = {
            rights_view: 1,
            rights_edit: 1,
            rights_add: 1,
            rights_delete: 1,
            rights_view_deleted: 1,
            object_id: current_page[0].object_id,
            reporting_access_view_ids: [],
            reporting_access_view_all: 1,
            reporting_access_edit_ids: [],
            reporting_access_edit_all: 1,
            reporting_access_delete_ids: [],
            reporting_access_delete_all: 1,
            page_url: location.pathname,
          };
        } else {
          current_page_permissions = {
            rights_view: current_page[0].rights_view == "1" ? 1 : 0,
            rights_edit: current_page[0].rights_edit == "1" ? 1 : 0,
            rights_add: current_page[0].rights_add == "1" ? 1 : 0,
            rights_delete: current_page[0].rights_delete == "1" ? 1 : 0,
            rights_view_deleted:
              current_page[0].rights_view_deleted == "1" ? 1 : 0,
            object_id: current_page[0].object_id,
            page_url: location.pathname,
          };

          //defining access ids
          const access_specifier_array = ["view", "edit", "delete"];

          access_specifier_array.map((solo) => {
            if (
              current_page[0]["reporting_access_" + solo].includes("all")
            ) {
              current_page_permissions = {
                ...current_page_permissions,
                ["reporting_access_" + solo + "_all"]: 1,
                ["reporting_access_" + solo + "_ids"]: [],
              };
            } else {
              let access_ids = [];
              if (
                current_page[0]["reporting_access_" + solo].includes("self")
              ) {
                access_ids = access_ids.concat(session.user.id);
              }

              current_page_permissions = {
                ...current_page_permissions,
                ["reporting_access_" + solo + "_all"]: 0,
                ["reporting_access_" + solo + "_ids"]: access_ids,
              };
            }
          });
        }

        // Set permissions once — do not clear to [] first (that causes update-depth storms
        // while Appraisal Setup Form / CKEditor are still mounted during route changes).
        dispatch(setCurrentPagePermissions(current_page_permissions));
        if (
          page_type === "view" &&
          current_page_permissions.rights_view == "1"
        ) {
        } else if (page_type === "update" || page_type === "edit") {
        } else if (
          page_type === "add" &&
          current_page_permissions.rights_add == "1"
        ) {
        } else {
          //navigate('errors/error-401');
        }
      } else {
        //navigate('errors/error-404');
      }
    }
    // Only react to path changes. List pages update location.search for filters;
    // depending on search re-ran this effect in a loop with setSearchParams.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const element = useRoutes(routes);
  return element;
}

const Main = () => {
  const dispatch = useDispatch();
  const session = useSelector((state) => state.session);

  const token = new URLSearchParams(window.location.search).get("token");
  const [loading, setLoading] = useState(token ? true : false);

  useEffect(() => {
    if (token) { //alert('sss');
      
      dispatch(verifyUserToken(token));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { 
    if(!isEmpty(token)){
      if (session.loggedIn ) { 
        //setLoading(false);
        
        /*setTimeout(function() {  
          window.location.replace(APP_URL);
        }, 1000);*/

      }
    }
    else{
      if (!session.loggedIn) {
        setLoading(false);

        setTimeout(function() { 
         window.location.replace(LOGIN_URL + "login?redirect_url=" + DASHBOARD_URL);
        }, 3000);
      }
      else{

        // Function to check token expiration and logout
        const checkTokenExpiration = () => { 
          const token = localStorage.getItem('token');
          const expiration_at = localStorage.getItem('expiration_at');
          const expirationTime = new Date(expiration_at).getTime();
          const now = new Date().getTime();

          if (expirationTime <= now && token) {
            // Token is expired, dispatch logout
            dispatch(logout());
          }
          
        };
        // Set interval to check token expiration every minute (adjust as needed)
        setInterval(checkTokenExpiration, 60000);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.loggedIn]);

  useEffect(() => {
    if (session.timezone_offset) {
      moment.tz.setDefault(session.timezone_offset);
    }
  }, [session.timezone_offset]);

  
        

  if (loading) {
    return <VerificationLoader />;
  }

  return (
    <div className="App wrapper">
      <Router>
        <Suspense fallback={<SuspenseLoader />}>
          <AppRoutes />
        </Suspense>
      </Router>
    </div>
  );
};

export default Main;
